import React, { useEffect } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/core/success';
import WarningIcon from '@atlaskit/icon/core/warning';
import CheckCircleIconOld from '@atlaskit/icon/glyph/check-circle';
import ChangeIndicator from '@atlaskit/icon/glyph/hipchat/media-attachment-count';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalTitle,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import { usePopupController } from '@atlassian/jira-servicedesk-common/src/use-popup-controller';
import {
	useFlaggedTranslationCount,
	useManageTranslationsActions,
	useFormDirty,
	useTranslationStoreState,
} from '../../controllers/manage-translations/index.tsx';
import { useTranslationsSaveDataService } from '../../services/save-data';
import type { LanguageTranslationsSavePayload } from '../../services/save-data/types';
import { buildTranslationsSaveDataPayload } from '../../services/utils';
import messages from './messages';
import { FooterWrapper, Title, SubTitle, ButtonWrapper, Status, FooterContent } from './styled';

export const Footer = () => {
	const { projectKey } = useCommonPageContext();
	const { formatMessage } = useIntl();
	const data = useTranslationStoreState();
	const { discardChanges, onFooterSaveInitiated, onFooterSaveSuccess, onFooterSaveFailure } =
		useManageTranslationsActions();
	const isFormDirty = useFormDirty();
	const flaggedPanelsCount = useFlaggedTranslationCount();
	const [discardModalOpen, toggleDiscardModal] = usePopupController(false);
	const { showFlag } = useFlagsService();
	const [{ loading, error, translationsSaved }, { saveLanguageTranslationsData }] =
		useTranslationsSaveDataService({
			projectKey,
		});

	const onSave = () => {
		onFooterSaveInitiated();
		const { selectedPanelId, translationPanelMap, currentLanguage } = data;

		if (translationPanelMap && selectedPanelId && saveLanguageTranslationsData) {
			const payload: LanguageTranslationsSavePayload = buildTranslationsSaveDataPayload({
				selectedPanelId,
				translationPanelMap,
				currentLanguage,
			});
			saveLanguageTranslationsData(payload);
		}
	};

	useEffect(() => {
		if (error) {
			const errorMessage =
				error instanceof FetchError && error.statusCode === 403
					? messages.flagErrorMessage
					: messages.genericErrorMessage;

			showFlag({
				type: 'error',
				isAutoDismiss: true,
				title: formatMessage(errorMessage),
			});
			onFooterSaveFailure();
		}
	}, [error, onFooterSaveFailure, formatMessage, showFlag]);

	useEffect(() => {
		if (translationsSaved?.isSuccess) {
			showFlag({
				type: 'success',
				isAutoDismiss: true,
				title: formatMessage(messages.translationsSaveSuccess),
			});
			onFooterSaveSuccess();
		}
	}, [formatMessage, onFooterSaveSuccess, showFlag, translationsSaved]);

	if (!data?.translationPanelMap) {
		return null;
	}

	// Inline when cleaning up jira_nav4
	const FlaggedIcon = getWillShowNav4() ? (
		<WarningIcon
			color={token('color.icon.warning')}
			label=""
			testId="language-settings.ui.manage-translations-page-footer.flagged-panels-indicator"
			spacing="spacious"
		/>
	) : (
		// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
		<ChangeIndicator
			primaryColor={token('color.icon.warning')}
			label=""
			testId="language-settings.ui.manage-translations-page-footer.flagged-panels-indicator"
		/>
	);

	// Inline when cleaning up jira_nav4
	const CheckCircleIconComponent = getWillShowNav4() ? (
		<CheckCircleIcon
			spacing="spacious"
			color={token('color.icon.success')}
			label=""
			testId="language-settings.ui.manage-translations-page-footer.no-flagged-panels-indicator"
		/>
	) : (
		<CheckCircleIconOld
			primaryColor={token('color.icon.success')}
			label=""
			testId="language-settings.ui.manage-translations-page-footer.no-flagged-panels-indicator"
		/>
	);

	return (
		<FooterWrapper data-testid="language-settings.ui.manage-translations-page-footer.footer-wrapper">
			<FooterContent>
				<Status>
					<Title>
						{formatMessage(messages.footerTitle, {
							language: data?.selectedLanguage?.label,
						})}
					</Title>
					<SubTitle data-testid="language-settings.ui.manage-translations-page-footer.flagged-panels-count">
						{flaggedPanelsCount === 0 ? <>{CheckCircleIconComponent}</> : <>{FlaggedIcon}</>}
						{formatMessage(messages.footerSubTitle, {
							needsReviewCount: flaggedPanelsCount,
						})}
					</SubTitle>
				</Status>
				<ButtonWrapper data-testid="language-settings.ui.manage-translations-page-footer.button-wrapper">
					<ButtonGroup>
						<Button
							testId="language-settings.ui.manage-translations-page-footer.discard-button"
							onClick={toggleDiscardModal}
							isDisabled={!isFormDirty}
						>
							{formatMessage(messages.discardButton)}
						</Button>
						<LoadingButton
							testId="language-settings.ui.manage-translations-page-footer.save-button"
							appearance="primary"
							isDisabled={!isFormDirty}
							isLoading={loading}
							onClick={onSave}
						>
							{formatMessage(messages.saveButton)}
						</LoadingButton>
					</ButtonGroup>
					<ModalTransition>
						{discardModalOpen && (
							<ShortcutScope>
								<ModalDialog>
									<ModalHeader>
										<ModalTitle>{formatMessage(messages.discardModalTitle)}</ModalTitle>
									</ModalHeader>
									<ModalBody>{formatMessage(messages.discardModalBody)}</ModalBody>
									<ModalFooter>
										<Button appearance="subtle" onClick={toggleDiscardModal}>
											{formatMessage(messages.cancelButton)}
										</Button>
										<Button
											testId="language-settings.ui.manage-translations-page-footer.modal-discard-button"
											appearance="primary"
											onClick={() => {
												discardChanges();
												toggleDiscardModal();
											}}
										>
											{formatMessage(messages.discardButtonOnModal)}
										</Button>
									</ModalFooter>
								</ModalDialog>
							</ShortcutScope>
						)}
					</ModalTransition>
				</ButtonWrapper>
			</FooterContent>
		</FooterWrapper>
	);
};

export default Footer;
